import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, Input, Select } from "antd";
import React, { useEffect, useRef } from "react";
import {
  createClient,
  passJenkinsPayload,
  s3BucketCreateTrigger,
  updateClient,
} from "../../../../api/apiCallManager"; // Ensure this path is correct
import useClientInfoList from "../../../../components/hooks/useClientInfoList";
import { useRegions } from "../../../../components/hooks/useRegions";
import useUserListByRegion from "../../../../components/hooks/useUserListByRegion";
import FormLabel from "../../../../components/label/FormLabel";
import Loader from "../../../../components/spinner/Loader";
import { showError, showSuccess } from "../../../../helpers/alerts";
import { requiredValidator } from "../../../../helpers/formValidatorRules";
import { LOGGER } from "../../../../helpers/logger";
import useModalStore from "../../../../zustand/store/useModalStore";

const { Option } = Select;

const ManageClientForm = ({ clientEditData }) => {
  // const [s3BucketParams, setS3BucketParam] = useState("");
  // const isUpdateRef = useRef(false);
  const { data: clientInfoList, error, isLoading } = useClientInfoList();
  const formValuesRef = useRef(null);
  const jenkinsPayload = useRef(null);
  const [form] = Form.useForm();
  const { closeModal } = useModalStore((state) => ({
    closeModal: state.closeModal,
  }));
  const {
    data: regionList,
    error: regionError,
    isLoading: regionLoading,
  } = useRegions();
  // const { rolesList } = useRolesListStore();
  const region = Form.useWatch("region", form);
  const { data: clientManagers } = useUserListByRegion(
    region,
    "Client_Manager"
  );
  const { data: siteManagers } = useUserListByRegion(region, "Site_Manager");
  const queryClient = useQueryClient();
  const createS3BucketMutation = useMutation({
    mutationFn: s3BucketCreateTrigger,
    onSuccess: (data) => {
      if (data.statusCode === 200) {
        showSuccess("S3 Initiated successfully!");
        jenkinsPayload.current = data.body;
        createClientMutation.mutate(formValuesRef.current);

        // passJenkinsPayloadAPI.mutate(jenkinsPayload.current);
        // queryClient.invalidateQueries("getClientInfoListKey");
        // closeModal("manageClient-Modal-Id");
      }
    },
    onError: () => {
      showError("S3 Provision Failed!");
    },
  });
  const passJenkinsPayloadAPI = useMutation({
    mutationFn: passJenkinsPayload,
    onSuccess: (data) => {
      if (data.statusCode === 200) {
        showSuccess("S3 Provisioned successfully!");
        createClientMutation.mutate(formValuesRef.current);
        // queryClient.invalidateQueries("getClientInfoListKey");
        // closeModal("manageClient-Modal-Id");
      }
    },
    onError: () => {
      showError("S3 Provision Failed!");
    },
  });

  const createClientMutation = useMutation({
    mutationFn: createClient,
    onSuccess: (data) => {
      if (data.statusCode === 200) {
        showSuccess("Client created successfully!");
        queryClient.invalidateQueries({ queryKey: ["getClientInfoListKey"] });
        closeModal("manageClient-Modal-Id");
      }
    },
  });
  const updateClientMutation = useMutation({
    mutationFn: updateClient,
    onSuccess: (data) => {
      if (data.statusCode === 200) {
        showSuccess("Client updated successfully!");
        queryClient.invalidateQueries({ queryKey: ["getClientInfoListKey"] });
        closeModal("manageClient-Modal-Id");
      }
    },
  });

  useEffect(() => {
    if (clientEditData) {
      const initialSiteManager = clientEditData.siteManager.map(
        (manager) => manager.email
      );
      form.setFieldsValue({
        ...clientEditData,
        siteManager: initialSiteManager,
      });
    }
  }, [clientEditData, form]);

  const onFinish = (values) => {
    LOGGER(values);
    // Find the corresponding siteManager objects using the email values
    const formattedSiteManager = values?.siteManager?.map((email) => {
      const manager = siteManagers.find((item) => item.email === email);
      return {
        email: manager.email,
        siteName: manager.siteName,
      };
    });
    // LOGGER(formattedSiteManager);

    formValuesRef.current = {
      siteManager: formattedSiteManager,
      clientManager: values.clientManager,
      clientName: values.clientName,
      region: values.region,
    };
    if (clientEditData) {
      updateClientMutation.mutate({
        record_id: clientEditData?.key,
        siteManager: formattedSiteManager,
        clientManager: values.clientManager,
        clientName: values.clientName,
        region: values.region,
      });
    } else {
      createS3BucketMutation.mutate({
        record_id: clientEditData?.key,
        siteManager: formattedSiteManager,
        clientManager: values.clientManager,
        clientName: values.clientName,
        region: values.region,
      });
    }
  };
  const handleRegionChange = (value) => {
    form.setFieldsValue({
      clientManager: undefined,
      siteManager: undefined,
    });
  };
  //If you want to remove the already existing presented region then use below function
  // const nonPresentedRegions = regionList?.filter(
  //   (item) => !clientInfoList.some((info) => item.includes(info.region))
  // );
  // const presentedRegions = regionList?.filter((item) =>
  //   clientInfoList?.some((info) => item?.includes(info?.region))
  // );

  const s3BucketNameValidator = (_, value) => {
    const s3Regex = /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/;
    if (!value) {
      return Promise.reject("Client name is required.");
    }
    if (!s3Regex.test(value)) {
      return Promise.reject(
        "Invalid name! Use only lowercase letters, numbers, hyphens, and dots (3-63 chars)."
      );
    }
    return Promise.resolve();
  };
  return (
    <Form
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={onFinish}
      initialValues={clientEditData}
    >
      {/* {JSON.stringify(nonPresentedRegions)} */}
      <Form.Item
        name="clientName"
        label={<FormLabel>Client Name</FormLabel>}
        rules={[{ validator: s3BucketNameValidator }]}
      >
        <Input
          placeholder="Enter valid S3 bucket name"
          disabled={clientEditData}
        />
      </Form.Item>

      <Form.Item
        name="region"
        label={<FormLabel>Region</FormLabel>}
        rules={[{ validator: requiredValidator }]}
      >
        <Select
          placeholder={
            regionLoading
              ? "Loading region list, please wait..."
              : "Select Region"
          }
          loading={regionLoading}
          disabled={regionLoading || clientEditData}
          onChange={handleRegionChange}
          showSearch
        >
          {regionList &&
            regionList.map((region) => (
              <Option
                key={region}
                value={region}
                // disabled={presentedRegions.includes(region)}
              >
                {region}
              </Option>
            ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="clientManager"
        label={<FormLabel>Client Manager</FormLabel>}
        rules={[{ validator: requiredValidator }]}
      >
        <Select
          mode="single"
          disabled={!clientManagers}
          placeholder="Select a client manager"
        >
          {clientManagers?.map((manager) => (
            <Option key={manager.id} value={manager.email}>
              {manager.userName} ({manager.email})
            </Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="siteManager"
        label={<FormLabel>Sites</FormLabel>}
        rules={[{ validator: requiredValidator }]}
      >
        <Select
          mode="multiple"
          disabled={!siteManagers}
          placeholder="Select a site manager"
        >
          {siteManagers?.map(
            (manager, index) =>
              manager.siteName && (
                <Option key={index} value={manager.email}>
                  {manager.siteName}
                </Option>
              )
          )}
        </Select>
      </Form.Item>
      <div className="py-1"></div>
      <Form.Item>
        <button
          className="bg-[#0057B8] py-2 rounded-sm text-[13px] w-[100%] text-white"
          type="submit"
          disabled={createS3BucketMutation.isPending}
        >
          <Loader isLoading={createS3BucketMutation.isPending} />
          {clientEditData ? "Update" : "Submit"}
        </button>
      </Form.Item>
    </Form>
  );
};

export default ManageClientForm;
